import moment from "moment";

/**
 * A pay-later reservation with nothing paid on it yet (1NOW-11476). Its booking page still shows everything about the
 * booking (trip, invoice, agreement, renter), with a Pay and book notice on top, but not the actions: no Edit, Cancel,
 * ID or insurance verification and uploads, trip photos, or other bookings, until it is paid.
 *
 * The Template's rule (reservationUnpaid.ts there, which reads the same fields through booking-core's paymentState):
 * read from the booking itself, not from the way the renter got to the page. A card booking, a paid reservation and
 * a partly paid one are not this, and show as they always have. A later cycle of a long-term stay
 * (`main_booking_uuid` set) is not the reservation either: a renter mid-stay keeps the whole page while the next
 * cycle is unpaid.
 */
export const reservationUnpaid = (booking: any): boolean =>
  booking?.pay_later === true &&
  booking?.payment_status === "un-paid" &&
  !booking?.main_booking_uuid;

const num = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** What the booking asks for now, without the refundable deposit; null when the invoice does not say. */
export const amountDue = (booking: any): number | null =>
  num(booking?.invoice?.total_price_after_coupon_without_security) ??
  num(booking?.invoice?.total_price_without_security) ??
  num(booking?.final_price_without_security);

/**
 * "Thu, Oct 9": the day a pay-later booking is to be paid by. The backend's `payment_due_date` when the booking
 * carries one, else the day before pick-up (what the Template shows); "" when there is no date.
 */
export const payByText = (booking: any): string => {
  const given = booking?.payment_due_date;
  const start = booking?.pick_up_time;
  const day = given
    ? moment.parseZone(given)
    : start
    ? moment.parseZone(start).subtract(1, "day")
    : null;
  return day?.isValid() ? day.format("ddd, MMM D") : "";
};

/** $1,234.50 */
export const dollars = (n: number): string =>
  `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
